/**
 * Hard-reload once when a dynamic chunk import fails.
 *
 * Cause: SPA deploys delete old `_nuxt/<hash>.js` chunks. A tab opened before
 * the deploy holds an `index.html` that points at the now-missing hashes;
 * route navigation triggers a dynamic import that 404/503s and Nuxt surfaces
 * "Failed to fetch dynamically imported module" / "Importing a module script
 * failed" / "ChunkLoadError".
 *
 * Fix: force `window.location.reload()` so the browser pulls the new
 * `index.html` (which points at the current chunk hashes). Capped at
 * MAX_ATTEMPTS reloads per URI (path) within a tab session — after that we
 * surface the underlying error instead of looping on a persistent failure
 * (CloudFront throttling Safari preloads, broken deploy, etc.).
 *
 * Why per-URI attempts instead of a boolean cleared on `router.afterEach`:
 * Safari frequently fails to import a *lazy* module after the initial
 * route navigation has already succeeded. An `afterEach`-cleared boolean
 * would be wiped on that first successful navigation, so the next chunk
 * failure would reload again — looping forever. A per-URI counter
 * survives reloads (sessionStorage), so the cap actually bites.
 */
const RELOAD_COUNTS_KEY = 'nuxt_chunk_reload_counts'
const MAX_ATTEMPTS = 3
const ERROR_PATTERN = /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError|error loading dynamically imported module/i

function shouldReload(error: unknown): boolean {
  const msg = (error as Error | undefined)?.message ?? String(error ?? '')
  return ERROR_PATTERN.test(msg)
}

function readCounts(): Record<string, number> {
  try {
    const raw = sessionStorage.getItem(RELOAD_COUNTS_KEY)
    return raw ? JSON.parse(raw) as Record<string, number> : {}
  } catch {
    return {}
  }
}

function writeCounts(counts: Record<string, number>) {
  try {
    sessionStorage.setItem(RELOAD_COUNTS_KEY, JSON.stringify(counts))
  } catch {
    // sessionStorage unavailable (private mode / quota) — reload cap just
    // won't persist; tryReload still works within this page lifetime.
  }
}

function tryReload(reason: string) {
  const uri = window.location.pathname
  const counts = readCounts()
  const n = counts[uri] ?? 0
  if (n >= MAX_ATTEMPTS) {
    // Hit the per-URI cap — surface the underlying error rather than loop.
    // The chunk is either persistently broken or the network is dropping
    // it; further reloads won't help.
    console.error(`[chunk-reload] max ${MAX_ATTEMPTS} reloads reached for ${uri}; not retrying:`, reason)
    return false
  }
  counts[uri] = n + 1
  writeCounts(counts)
  console.warn(`[chunk-reload] hard-reloading (${n + 1}/${MAX_ATTEMPTS}) due to`, reason)
  window.location.reload()
  return true
}

export default defineNuxtPlugin((nuxtApp) => {
  // A successful mount proves chunks are loading again — wipe the attempt
  // ledger so a tab that capped out during an outage self-heals instead of
  // surfacing the error forever (mobile tabs keep sessionStorage for days).
  // Any later failure re-earns its MAX_ATTEMPTS; a reload loop would still
  // need a fresh failure per attempt, and mount-success means real progress.
  nuxtApp.hook('app:mounted', () => {
    try {
      sessionStorage.removeItem(RELOAD_COUNTS_KEY)
    } catch {
      // sessionStorage unavailable — nothing to clear.
    }
  })

  // 1. Vue Router navigation errors (the most common path)
  const router = useRouter()
  router.onError((error) => {
    if (shouldReload(error)) {
      tryReload(`router error: ${(error as Error).message}`)
    }
  })

  // 2. Vue lifecycle errors that bubble up (e.g. async component import in setup)
  nuxtApp.vueApp.config.errorHandler = (error, _instance, info) => {
    if (shouldReload(error)) {
      tryReload(`vue errorHandler [${info}]: ${(error as Error)?.message}`)
    }
  }

  // 3. Vite preload errors (fires before the rejection bubbles) — most direct signal.
  window.addEventListener('vite:preloadError', (event) => {
    tryReload(`vite:preloadError: ${(event as unknown as { payload?: Error }).payload?.message ?? 'unknown'}`)
  })

  // 4. Unhandled promise rejections (e.g. inflight import() outside the router)
  window.addEventListener('unhandledrejection', (event) => {
    if (shouldReload(event.reason)) {
      tryReload(`unhandledrejection: ${(event.reason as Error)?.message ?? event.reason}`)
    }
  })
})
